import { RefreshCw, Rocket } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { BUILD_ID, fetchServerVersion, reloadForNewVersion } from "@/lib/version";

const POLL_INTERVAL_MS = 3 * 60_000;

/**
 * Watches for a newer deployment and, when one lands, offers a refresh — before
 * the user navigates into a purged chunk and gets a hard failure. This is the
 * graceful half of cache-busting: proactive and non-blocking, so a deploy
 * mid-session is a gentle nudge rather than a crash.
 *
 * Checks on mount, whenever the tab regains focus (the moment most likely to
 * follow a deploy), on reconnect, and on a slow interval as a backstop.
 */
export function UpdatePrompt() {
  const [updateReady, setUpdateReady] = React.useState(false);
  const [reloading, setReloading] = React.useState(false);

  React.useEffect(() => {
    if (BUILD_ID === "dev") return; // no manifest under the dev server

    let cancelled = false;
    const controller = new AbortController();

    const check = async () => {
      if (cancelled || document.visibilityState === "hidden") return;
      const server = await fetchServerVersion(controller.signal);
      if (!cancelled && server && server !== BUILD_ID) setUpdateReady(true);
    };

    void check();
    const interval = window.setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => void check();

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("online", onFocus);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("online", onFocus);
    };
  }, []);

  if (!updateReady) return null;

  return (
    <div
      role="status"
      className={cn(
        "safe-bottom fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4",
        "animate-in slide-in-from-bottom-4 fade-in",
        "lg:bottom-4 lg:justify-end lg:pr-6",
      )}
    >
      <div className="glass flex w-full max-w-md items-center gap-3 rounded-xl border p-3 shadow-2xl">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary">
          <Rocket className="size-4.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">A new version is ready</p>
          <p className="text-xs text-muted-foreground">Refresh to pick it up — it takes a second.</p>
        </div>
        <Button
          size="sm"
          loading={reloading}
          onClick={() => {
            setReloading(true);
            reloadForNewVersion();
          }}
        >
          {!reloading && <RefreshCw className="size-3.5" />}
          Refresh
        </Button>
      </div>
    </div>
  );
}
